import type {AxiosInstance, AxiosResponse, InternalAxiosRequestConfig} from 'axios';
import axios, {AxiosError} from 'axios';

import type {Environment, Tokens} from './session';
import {decodeAccessToken, expiresWithin} from './jwt';
import {redirectTo} from '../components/base/routing/partials/routing-util';
import {clearSession, getPolyshoppenUser, getTokens, reloadTokens, setTokens} from './session';
import {leaveBreadcrumb, reportError} from './error-reporter';
import {getErrorCode, getOutcome, getReportText} from './api-error';
import {getLanguage} from './i18n';
import {forgetIdempotencyKey, idempotencyKeyFor} from './idempotency-keys';

declare module 'axios' {
    interface AxiosRequestConfig {
        /** set on the retry of a request that failed with a 401, so it is retried only once */
        authRetried?: boolean;
    }
}

type TokenResponse = {
    access_token: string;
    refresh_token: string;
};

/**
 * The terminal and its build, as `X-App` (a browser app cannot set its `User-Agent`): the API keeps the application id
 * as the app type in the order log. The build id is no semver version, so no minimum version applies to it.
 */
const xApp = `nl.polyestershoppen.terminal/${__BUILD_ID__}`;

/** refresh the access token when it expires within this many seconds */
const refreshMargin = 30;

/**
 * (ms) The deadline of a request: a connection that hangs (Wi-Fi gone mid-request) must not keep the terminal waiting
 * for an order, or every request waiting for the token refresh. Shipping actions have their own (`actionTimeout`).
 */
const requestTimeout = 20000;

/** The number of API requests underway, for the activity bar of the top bar (see `useRequesting`). */
let requests = 0;
const requestListeners = new Set<() => void>();

const countRequests = (change: number): void => {
    requests += change;
    requestListeners.forEach(listener => listener());
};

export const subscribeToRequests = (listener: () => void): (() => void) => {
    requestListeners.add(listener);

    return () => requestListeners.delete(listener);
};

export const isRequesting = (): boolean => 0 < requests;

/** The default adapter (XHR or fetch), counting the requests it sends until each is answered or fails. */
const sendRequest = axios.getAdapter(axios.defaults.adapter);

export const getBaseUrl = (environment: Environment): string =>
    `https://${environment === 'development' ? 'development.' : ''}polyestershoppen.nl`;

/**
 * A path on the API's host (like `/admin/order/877405`): the terminal's own host would serve its index.html for it. A
 * full URL stays as it is.
 */
export const apiUrl = (path: string, environment: Environment): string => new URL(path, getBaseUrl(environment)).href;

/** The page of an order in the admin of the environment. */
export const adminOrderUrl = (orderId: number | string, environment: Environment): string =>
    apiUrl('/admin/order/' + orderId, environment);

/**
 * A client for the API of the environment. It asks for JSON only: the API answers some errors with an HTML page
 * otherwise. And it asks for the language the terminal speaks, which can be one the user picked over the locale of
 * their account, so the API's messages are in that language.
 */
const createClient = (environment: Environment): AxiosInstance => {
    const client = axios.create({
        baseURL: getBaseUrl(environment),
        timeout: requestTimeout,
        headers: {Accept: 'application/json', 'X-App': xApp},
        adapter: async config => {
            countRequests(1);

            try {
                return await sendRequest(config);
            } finally {
                countRequests(-1);
            }
        },
    });

    client.interceptors.request.use(config => {
        config.headers.set('Accept-Language', getLanguage());

        return config;
    });

    return client;
};

/** Endpoints that must not get the bearer token: the public user list and the token endpoints themselves. */
const isPublicEndpoint = (url: string | undefined): boolean => {
    const path = (url ?? '').split('?')[0];

    return '/api/users' === path || path.startsWith('/api/v2/token');
};

/** The tokens were rejected: end the session and start over on the default environment (the shipping station is kept). */
const endSession = (): void => {
    // the operator is sent to the login page in the middle of their work: why is worth knowing
    reportError(
        new Error('The API rejected the refresh token: the session ended'),
        {section: 'session', values: {user: getPolyshoppenUser()?.username ?? null}},
        'warning',
        'Session ended',
    );
    clearSession();
    redirectTo('/login');
};

/** Never settles: for requests that are dropped because the user is sent back to the login page. */
// (a function declaration: Babel parses `.ts` files with JSX enabled, so `<T>() =>` would be ambiguous)
function abandoned<T>(): Promise<T> {
    return new Promise<T>(() => undefined);
}

const requestRefresh = async (environment: Environment, seenRefreshToken: string | undefined): Promise<Tokens> => {
    // another tab may have rotated the tokens (or ended the session) while this one waited for the lock
    const current = reloadTokens();
    const usedRefreshToken = current?.refreshToken;

    if (!usedRefreshToken) {
        throw new Error('No refresh token');
    }

    if (seenRefreshToken && usedRefreshToken !== seenRefreshToken) {
        return current;
    }

    try {
        const {data} = await createClient(environment).post<TokenResponse>('/api/v2/token/refresh', undefined, {
            headers: {'X-Refresh-Token': usedRefreshToken},
        });

        // the session was ended (logout, idle) or replaced (a new login) while the refresh was underway: drop the result
        if (getTokens()?.refreshToken !== usedRefreshToken) {
            throw new Error('The session changed during the refresh');
        }

        setTokens(data.access_token, data.refresh_token, false);
        leaveBreadcrumb('Token refreshed', undefined, 'request');

        // just stored by setTokens
        return getTokens()!;
    } catch (error) {
        // the token endpoint failing; offline it is not answered at all, the request it was for reports that
        if (axios.isAxiosError(error) && error.response && 401 !== error.response.status) {
            reportApiFailure('Token Refresh', error);
        } else if (axios.isAxiosError(error) && 401 === error.response?.status) {
            // refresh tokens are single use: another tab may have rotated it in the meantime
            const latest = reloadTokens();

            if (latest && latest.refreshToken !== usedRefreshToken) {
                return latest;
            }

            if (latest) {
                endSession();
            }
        }

        throw error;
    }
};

/** Refresh the tokens, serialised across tabs (when supported) as every tab shares the same single use token. */
const refresh = (environment: Environment): Promise<Tokens> => {
    const seenRefreshToken = getTokens()?.refreshToken;

    if (!navigator.locks) {
        return requestRefresh(environment, seenRefreshToken);
    }

    return navigator.locks.request('shipping-terminal.token-refresh', () =>
        requestRefresh(environment, seenRefreshToken),
    );
};

let refreshing: Promise<Tokens> | null = null;

/** Refresh the tokens, concurrent callers share the same request (a refresh token can only be used once). */
const refreshTokens = (environment: Environment): Promise<Tokens> =>
    (refreshing ??= refresh(environment).finally(() => {
        refreshing = null;
    }));

/**
 * The current access token, refreshed first when it (nearly) expired. When the refresh fails, the request fails with
 * that error (it would only be refused with the expired token); when the session ended, it is not sent at all.
 */
const getAccessToken = async (environment: Environment): Promise<string | null> => {
    const tokens = getTokens();

    if (!tokens) {
        return null;
    }

    // a token is only valid on the API that issued it, e.g. after another tab switched the environment
    if (decodeAccessToken(tokens.accessToken)?.iss !== getBaseUrl(environment)) {
        return null;
    }

    if (!expiresWithin(decodeAccessToken(tokens.accessToken), refreshMargin)) {
        return tokens.accessToken;
    }

    try {
        return (await refreshTokens(environment)).accessToken;
    } catch (error) {
        // the session ended and the user is sent to the login page: the request is dropped
        if (!getTokens()) {
            return abandoned<string | null>();
        }

        // the session changed meanwhile (a new login in another tab): its token
        if (!axios.isAxiosError(error)) {
            return getTokens()?.accessToken ?? null;
        }

        throw error;
    }
};

/**
 * The client for the polyestershoppen.nl API of the given environment. It authenticates requests with the
 * bearer token of the logged in user, refreshing it when needed.
 */
export const getInstance = (environment: Environment): AxiosInstance => {
    const instance = createClient(environment);

    instance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
        if (!isPublicEndpoint(config.url)) {
            // the user logged out (e.g. in another tab) while this terminal was still in use
            if (!getPolyshoppenUser()) {
                redirectTo('/login');

                return abandoned<InternalAxiosRequestConfig>();
            }

            const accessToken = await getAccessToken(environment);

            if (accessToken) {
                config.headers.set('Authorization', `Bearer ${accessToken}`);
            }
        }

        return config;
    });

    instance.interceptors.response.use(undefined, async (error: AxiosError) => {
        const config = error.config;
        const tokens = getTokens();

        if (
            401 !== error.response?.status ||
            !config ||
            config.authRetried ||
            isPublicEndpoint(config.url) ||
            !tokens
        ) {
            throw error;
        }

        // refresh the tokens, unless another request already did so after this one was sent
        if (`Bearer ${tokens.accessToken}` === config.headers.get('Authorization')) {
            try {
                await refreshTokens(environment);
            } catch (refreshError) {
                if (!getTokens()) {
                    // the session ended and the user is sent to the login page: no error modal on top of it
                    return abandoned();
                }

                // why it could not be retried (offline, the token endpoint failing), not the 401 of the request
                throw refreshError;
            }
        }

        // the request interceptor adds the new access token
        return instance.request({...config, authRetried: true});
    });

    return instance;
};

/**
 * (ms) The deadline of a shipping action (printing, removing the shipments, a manco): the carrier and PrintNode can take
 * a while, but its dialog waits for the outcome and must not wait forever. Its outcome is unknown then (see `getOutcome`
 * in `api-error.ts`): the API may still be at it.
 */
const actionTimeout = 60000;

/** A shipping action: the request, and what to report about it when it fails. */
export interface ShippingAction {
    /** what the operator did, e.g. `Print Label`: Bugsnag groups the failures by it and their error code */
    title: string;
    orderId: number | string;
    method: 'post' | 'put' | 'delete';
    url: string;
    data?: unknown;
    /** sends an `Idempotency-Key`, the same one until the request succeeded (see `idempotency-keys.ts`) */
    idempotent?: boolean;
    /** the body is what the operator typed: an invalid one (422) is theirs to correct, not a bug */
    userInput?: boolean;
}

/**
 * Send a shipping action: it settles within `actionTimeout`, also while the request still waits for a token refresh
 * (see the interceptors above) or for the answer to its retry after a 401; the request is aborted then, so one that was
 * not sent yet is not sent any more. And when the terminal is left (`signal`): its rejection is not handled then, and
 * not reported. A failure is reported to Bugsnag (see `reportApiFailure`), the caller only handles what the operator
 * sees.
 */
export const sendAction = (
    environment: Environment,
    action: ShippingAction,
    signal?: AbortSignal,
): Promise<AxiosResponse> => {
    const {title, orderId, method, url, data} = action;
    const deadline = AbortSignal.timeout(actionTimeout);
    const idempotencyKey = action.idempotent ? idempotencyKeyFor(url, data) : undefined;
    const request = {
        method,
        url,
        data,
        headers: (idempotencyKey ? {'Idempotency-Key': idempotencyKey} : {}) as Record<string, string>,
        // (the deadline takes the place of the client's timeout)
        timeout: 0,
    };

    leaveBreadcrumb(`${title}: sent`, {orderId, method, url, idempotencyKey}, 'request');

    return new Promise<AxiosResponse>((resolve, reject) => {
        // like axios' own timeout: an error without a response, so its outcome is unknown
        const timedOut = (): void =>
            reject(
                new AxiosError(
                    `timeout of ${actionTimeout}ms exceeded`,
                    AxiosError.ETIMEDOUT,
                    request as unknown as InternalAxiosRequestConfig,
                ),
            );

        deadline.addEventListener('abort', timedOut, {once: true});
        getInstance(environment)
            .request({...request, signal: signal ? AbortSignal.any([signal, deadline]) : deadline})
            .then(resolve, reject)
            .finally(() => deadline.removeEventListener('abort', timedOut));
    }).then(
        response => {
            if (idempotencyKey) {
                forgetIdempotencyKey(url, data);
            }

            leaveBreadcrumb(
                `${title}: done`,
                {orderId, status: response.status, replayed: 'true' === response.headers['idempotent-replayed']},
                'request',
            );

            return response;
        },
        (error: unknown) => {
            if (!signal?.aborted) {
                reportApiFailure(title, error, {orderId, userInput: action.userInput, request});
            }

            throw error;
        },
    );
};

/** What is known about a failed request besides the error itself. */
interface FailureContext {
    orderId?: number | string;
    /** see `ShippingAction` */
    userInput?: boolean;
    /** the request as it was made: an error without a response (the deadline of `sendAction`) may lack it */
    request?: {method: string; url: string; data?: unknown; headers?: Record<string, string>};
}

/**
 * How bad a failed request is: an error (a bug, or labels created but not printed) or a warning (no answer: offline,
 * a timeout; an unexpected refusal), or just info when the operator was told and can handle it (the API refused it for
 * a reason it gave, another action was busy).
 */
const getSeverity = (error: unknown, userInput?: boolean): 'error' | 'warning' | 'info' => {
    if (!axios.isAxiosError(error)) {
        return 'error';
    }

    const {status, data} = error.response ?? {};
    const code = getErrorCode(data);

    if (undefined === status) {
        return 'warning';
    }

    switch (getOutcome(error)) {
        case 'printFailed':
            return 'error';
        case 'busy':
            return 'info';
        case 'unknown':
            // a server error, or a 502, 503 or 504 of the proxy
            return 'error';
    }

    if ('validation_failed' === code) {
        return userInput ? 'info' : 'warning';
    }

    return code && 'bad_request' !== code ? 'info' : 'warning';
};

/** A request body as JSON, for the report: axios has it as a string by then. */
const parseBody = (data: unknown): unknown => {
    if ('string' !== typeof data) {
        return data;
    }

    try {
        return JSON.parse(data);
    } catch {
        return data;
    }
};

/**
 * Report a failed request to Bugsnag: grouped by what was done (`title`) and its error code (else its status, else
 * axios' code: `ERR_NETWORK`, `ETIMEDOUT`), with the message of the API in English, and the request and its answer as
 * metadata. The severity says how much it matters (see `getSeverity`).
 */
export const reportApiFailure = (title: string, error: unknown, context: FailureContext = {}): void => {
    const axiosError = axios.isAxiosError(error) ? error : undefined;
    const {response, config} = axiosError ?? {};
    const code = getErrorCode(response?.data);
    const kind = code ?? (response ? `HTTP ${response.status}` : (axiosError?.code ?? 'Error'));
    const request = context.request ?? config;
    const headers = (request?.headers ?? {}) as Record<string, unknown>;
    const responseData = response?.data;
    const reported = new Error(`${kind}: ${getReportText(error)}`);

    // Bugsnag lists an error as its name and message: "Print Label" and "order_not_paid: The order isn't paid."
    reported.name = title;

    reportError(
        reported,
        {
            section: 'request',
            values: {
                orderId: context.orderId ?? null,
                method: request?.method ?? null,
                url: request?.url ?? null,
                body: parseBody(request?.data) ?? null,
                idempotencyKey: headers['Idempotency-Key'] ?? null,
                status: response?.status ?? null,
                code: code ?? null,
                outcome: axiosError ? getOutcome(error) : null,
                axiosCode: axiosError?.code ?? null,
                retryAfter: response?.headers['retry-after'] ?? null,
                idempotentReplayed: response?.headers['idempotent-replayed'] ?? null,
                // (an HTML page of the proxy only in part)
                response: 'string' === typeof responseData ? responseData.slice(0, 1000) : (responseData ?? null),
                message: axiosError?.message ?? (error instanceof Error ? error.message : String(error)),
            },
        },
        getSeverity(error, context.userInput),
        `${title}: ${kind}`,
    );
};
